import classnames from "classnames";
import { createContext, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import ProgressBar from "@ramonak/react-progress-bar";
import { BREAKPOINT } from "utils/constants";
import useWindowSize from "utils/windowSizeValue";
import ErrorScreen from "./components/ErrorScreen";
import {
  AvailableTrucks,
  Bids,
  Timer,
  RfqInformation,
  Modals,
  ButtonsGroup,
} from "./components";
import { getBidDetailsData, getRfqData, getRfqDataWithId, getByOrderIdData } from "./utils";
import styles from "./Dashboard.module.scss";
import { useLocation } from "react-router-dom";
import { MoreOptionsIcon } from "components/assets/Icons";
import Options from "./components/Options";
import useOnClickOutside from "utils/useOnClickOutside";
import {isEmpty} from "./../TruckEdit/Components/helpers";
import quoteService from "services/quote.service";

export const Context = createContext();

const Dashboard = () => {
  const { width } = useWindowSize();
  const [bidsData, setBidsData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [errorModal, setErrorModal] = useState({ state: false, type: "" });
  const [errorMessage, setErrorMessage] = useState("");
  const [rfqData, setRfqData] = useState({});
  const [bidDetailsData, setBidDetailsData] = useState({});
  const [percentage, setPercentage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [availableData, setAvailableData] = useState({});
  const [showDetailedInfo, setShowDetailedInfo] = useState(false);
  const [showByOrderId, setShowByOrderId] = useState(false);
  const [byOrderIdData, setByOrderIdData] = useState(null);
  const [selectedBid, setSelectedBid] = useState(null);
  const [emptyState, setEmptyState] = useState(false);
  const [showOptions, setShowOptions] = useState(null);
  const [showViewRfq, setShowViewRfq] = useState(false);
  const rfqId = localStorage.getItem("rfqID") ? localStorage.getItem("rfqID") : null;
  const [byId, setByid] = useState(null);
  const optionsRef = useRef();

  // AI Call Preview modal state
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiCallData, setAiCallData] = useState(null);
  const [aiCallLoading, setAiCallLoading] = useState(false);

  let { state } = useLocation();
  const location = useLocation();
  const routeState = location.state || {};

  // Query param fallback
  const params = new URLSearchParams(location.search);
  const dispatchedFromQuery = params.get("dispatched");
  const parsedDispatched = dispatchedFromQuery === "true";

  // Final `dispatched` value — prioritize `state` over query
  const dispatched = routeState.dispatched !== undefined
    ? routeState.dispatched
    : dispatchedFromQuery !== null
    ? parsedDispatched
    : false;

  // Final state object to pass to getRfqData
  const finalState = {
    ...routeState,
    dispatched,
  };
  const custom = useSelector((state) => state.customization);

  const contextValue = { bidsData, setBidsData, rfqData, setRfqData, bidDetailsData };
  const condition = width >= BREAKPOINT.media_xl;

  useEffect(() => {
    if (rfqId) {
      const take =
        localStorage.getItem("shouldTake") &&
        JSON.parse(localStorage.getItem("shouldTake"));
      getRfqDataWithId({
        setLoading,
        setPercentage,
        setRfqData,
        setErrorMessage,
        setEmptyState,
        rfqId,
        take,
        state: finalState,
      });
      setByid({ id: rfqId, take: take });
    } else {
      getRfqData({
        setLoading,
        setPercentage,
        setRfqData,
        setErrorMessage,
        setEmptyState,
        state: finalState,
      });
    }
    localStorage.removeItem("rfqID");
    return () => {
      localStorage.removeItem("rfqID");
      localStorage.removeItem("shouldTake");
    };
  }, [routeState.dispatched, dispatched]);

  useEffect(() => {
    showDetailedInfo && getBidDetailsData({ setBidDetailsData, selectedBid, setLoading });
  }, [selectedBid, showDetailedInfo]);

  useEffect(() => {
    showByOrderId && getByOrderIdData({ setByOrderIdData, showByOrderId, setLoading, setShowByOrderId })
  }, [showByOrderId]);

  useEffect(() => {
    setAvailableData({ id: rfqData.id, email: rfqData.customer?.agentEmail });
  }, [rfqData]);

  useOnClickOutside(optionsRef, () => setShowOptions(null));

  // Handle AI Call preview
  const handleAICallClick = async (rfqId, truckId) => {
    try {
      setAiCallLoading(true);
      setShowAIModal(true);
      setAiCallData(null); // Reset previous data

      const data = await quoteService.getCallPreview(rfqId, truckId);
      setAiCallData(data);
    } catch (error) {
      console.error('Error fetching AI call preview:', error);
      setErrorMessage('Failed to load AI call preview');
      // Close modal on error
      setShowAIModal(false);
    } finally {
      setAiCallLoading(false);
    }
  };

  return (
    <Context.Provider value={contextValue}>
      <section className={styles.section}>
        <div className={classnames(styles.leftSidebar, styles.column)}>
          <div className={styles.leftSidebarHeader}>
            <h2 className={classnames(styles.title, styles.titleLeftSidebar)}>MY BIDS</h2>
          </div>
          <div className={styles.bids}>
            <Bids
              setShowDetailedInfo={setShowDetailedInfo}
              setSelectedBid={setSelectedBid}
            />
          </div>
        </div>
        <div
          className={classnames(styles.mainPart, styles.column)}
          style={{ backgroundColor: `${showDetailedInfo ? "rgb(221, 221, 221)" : ""}` }}
        >
          <div className={styles.header}>
            <div className={classnames(styles.leftSide, styles.side)}>
              <h2 className={styles.title}>RFQ INFORMATION</h2>
              {rfqData.postedTimeStamp && !showDetailedInfo && (
                <Timer
                  className={styles.time}
                  postedTimeStamp={rfqData.postedTimeStamp}
                />
              )}
            </div>
            <div className={classnames(styles.side)}>
              <h2 className={styles.title}>AVAILABLE TRUCKS</h2>
              {rfqData.availableTrucks && (
                <span className={styles.trucks}>{rfqData.availableTrucks?.length}</span>
              )}
            </div>
            <ProgressBar
              completed={percentage}
              height={6}
              isLabelVisible={false}
              className={styles.progressBar}
              bgColor={"#" + custom.main_color}
              baseBgColor={"#" + custom.main_color}
              transitionTimingFunction="ease-in-out"
            />
          </div>

          {(!emptyState && !showDetailedInfo) || showDetailedInfo 
          || !isEmpty(byOrderIdData) 
          ? (
            <div className={styles.body}>
              <div className={styles.leftSide}>
                <RfqInformation
                  condition={condition}
                  rfqData={rfqData}
                  className={styles.card}
                  loadingClass={{ [styles.loading]: loading }}
                  showDetailedInfo={showDetailedInfo}
                  showByOrderId={showByOrderId}
                  byOrderIdData={byOrderIdData}
                />
                <ButtonsGroup
                  loading={loading}
                  accepted={accepted}
                  disabled={disabled}
                  setDisabled={setDisabled}
                  setShowModal={setShowModal}
                  setErrorModal={setErrorModal}
                  setErrorMessage={setErrorMessage}
                  setLoading={setLoading}
                  setPercentage={setPercentage}
                  setAccepted={setAccepted}
                  setAvailableData={setAvailableData}
                  showDetailedInfo={showDetailedInfo}
                  setShowDetailedInfo={setShowDetailedInfo}
                  setEmptyState={setEmptyState}
                  state={state}
                  byId={byId}
                  byOrderIdData={byOrderIdData}
                  setShowByOrderId={setShowByOrderId}
                  setShowViewRfq={setShowViewRfq}
                  setByOrderIdData={setByOrderIdData}
                />
              </div>
              <div
                className={classnames(styles.rightSide, { [styles.loading]: loading })}
              >
                {!loading && (
                  <>
                    <AvailableTrucks
                      rfqData={rfqData}
                      condition={condition}
                      setShowModal={setShowModal}
                      accepted={accepted}
                      setAvailableData={setAvailableData}
                      availableData={availableData}
                      showDetailedInfo={showDetailedInfo}
                      selectedBid={selectedBid}
                      state={state}
                      onAICallClick={handleAICallClick}
                    />
                  </>
                )}
                <button
                  className={styles.optionsBtn}
                  onClick={() => setShowOptions((prev) => !prev)}
                >
                  <MoreOptionsIcon />
                </button>
                {showOptions && (
                  <div ref={optionsRef}>
                    <Options
                      setShowOptions={setShowOptions}
                      setShowViewRfq={setShowViewRfq}
                      byOrderIdData={byOrderIdData}
                    />
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              <button
                className={styles.optionsBtn}
                onClick={() => setShowOptions((prev) => !prev)}
              >
                <MoreOptionsIcon />
              </button>
              {showOptions && (
                <div ref={optionsRef}>
                  <Options
                    setShowOptions={setShowOptions}
                    setShowViewRfq={setShowViewRfq}
                    byOrderIdData={byOrderIdData}
                  />
                </div>
              )}
            <ErrorScreen errorMessage={errorMessage} />
            </>
          )}
        </div>

        {(rfqData.id || showViewRfq) && (
          <Modals
            availableData={availableData}
            showModal={showModal}
            setShowModal={setShowModal}
            errorModal={errorModal}
            setErrorModal={setErrorModal}
            setShowViewRfq={setShowViewRfq}
            showViewRfq={showViewRfq}
            errorMessage={errorMessage}
            setErrorMessage={setErrorMessage}
            setLoading={setLoading}
            setPercentage={setPercentage}
            setDisabled={setDisabled}
            accepted={accepted}
            setAccepted={setAccepted}
            id={rfqData.id}
            setEmptyState={setEmptyState}
            state={state}
            rfqData={rfqData}
            byId={byId}
            setShowByOrderId={setShowByOrderId}
            byOrderIdData={byOrderIdData}
            showByOrderId={showByOrderId}
            showAIModal={showAIModal}
            setShowAIModal={setShowAIModal}
            aiCallData={aiCallData}
            aiCallLoading={aiCallLoading}
          />
        )}
      </section>
    </Context.Provider>
  );
};

export default Dashboard;
